/**
 * Preserve the intended in-app target across the login flow.
 *
 * A cold push click (`public/firebase-messaging-sw.js` opens
 * `/events/<public_id>` directly) that lands while signed out hits the
 * unauthenticated route table in App.tsx and bounces to `/login` — at
 * which point the requested path would otherwise be lost. This module is
 * the single place that remembers that target at the redirect moment and
 * hands it back to the post-auth navigation, for BOTH login transports
 * (password form via the authenticated `/login` route swap in App.tsx,
 * SSO via pages/AuthCallback.tsx — the dual-transport split).
 *
 * Not push-specific by design: any deep link that bounces through
 * `/login` (bookmark, shared URL) is preserved the same way.
 *
 * Storage: `sessionStorage`, deliberately.
 *   - Per-tab — the tab that showed the login form is the tab that
 *     intended to go somewhere; other tabs are unaffected.
 *   - Survives the SSO full-page round-trip to the provider and back
 *     (same tab, same origin), which react-router `location.state`
 *     does not — that's why this isn't router state.
 *   - Dropped on tab close, so a stale target can't leak into a
 *     next-day session.
 *
 * Open-redirect guard: only same-app path targets are ever stored or
 * returned. Absolute URLs (`https://…`), protocol-relative (`//evil`),
 * and the backslash variant (`/\evil`, which browsers normalise to
 * `//evil`) are rejected at BOTH the write and the read — the read-side
 * check also covers a tampered storage value.
 */

const RETURN_TO_KEY = 'aegix.one.v1.returnTo';

/**
 * True when `value` is a safe same-app navigation target: a string
 * starting with a single `/` (path-absolute), never protocol-relative
 * (`//…`) or its backslash spelling (`/\…`).
 */
export function isSafeReturnTarget(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.startsWith('/') &&
    !value.startsWith('//') &&
    !value.startsWith('/\\')
  );
}

/**
 * Remember `target` (path + search + hash) as the post-login landing.
 * Unsafe targets are ignored. Storage failures (private mode, quota)
 * degrade silently to the default: land on home.
 */
export function rememberReturnTo(target: string): void {
  if (!isSafeReturnTarget(target)) return;
  try {
    window.sessionStorage.setItem(RETURN_TO_KEY, target);
  } catch {
    // Storage unavailable — losing the deep target is non-fatal.
  }
}

/**
 * Read the remembered target without clearing it, or `null` when there
 * is none (or the stored value fails the safety check). Render-safe:
 * no side effects.
 */
export function peekReturnTo(): string | null {
  try {
    const value = window.sessionStorage.getItem(RETURN_TO_KEY);
    return isSafeReturnTarget(value) ? value : null;
  } catch {
    return null;
  }
}

/** Drop the remembered target. Idempotent. */
export function clearReturnTo(): void {
  try {
    window.sessionStorage.removeItem(RETURN_TO_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

/**
 * Read-and-clear in one step, for event/promise handlers (e.g. the
 * OAuth callback completion). Prefer `peekReturnTo` + `clearReturnTo`
 * inside React render/effect pairs — consuming during render is not
 * StrictMode/concurrent-safe.
 */
export function consumeReturnTo(): string | null {
  const target = peekReturnTo();
  clearReturnTo();
  return target;
}
